import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { TicketCartProvider } from "./context/TicketCartContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import CookieConsent from "./components/CookieConsent";
import { TicketCartDrawer, FloatingCartButton } from "./components/tickets";
import { PageTransition, SlideTransition } from "./components/PageTransition";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import ClubsPage from "./pages/ClubsPage";
import ClubDetailPage from "./pages/ClubDetailPage";
import DashboardPage from "./pages/DashboardPage";
import AddEditClubPage from "./pages/AddEditClubPage";
import AddEditEventPage from "./pages/AddEditEventPage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import AdminPage from "./pages/AdminPage";
import DJsPage from "./pages/DJsPage";
import DJDetailPage from "./pages/DJDetailPage";
import AddEditDJPage from "./pages/AddEditDJPage";
import InstructorProfilePage from "./pages/InstructorProfilePage";
import InstructorsPage from "./pages/InstructorsPage";
import PopularDancesPage from "./pages/PopularDancesPage";
import DJLivePage from "./pages/DJLivePage";
import DJConsolePage from "./pages/DJConsolePage";
import DJDisplayPage from "./pages/DJDisplayPage";
import DJProjectorPage from "./pages/DJProjectorPage";
import WeekendsPage from "./pages/WeekendsPage";
import WeekendDetailPage from "./pages/WeekendDetailPage";
import WeekendTermsPage from "./pages/WeekendTermsPage";
import ManageBookingPage from "./pages/ManageBookingPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ClassesPage from "./pages/ClassesPage";
import BingoHostPage from "./pages/BingoHostPage";
import BingoPlayPage from "./pages/BingoPlayPage";
import BingoJoinPage from "./pages/BingoJoinPage";
import BingoUK90HostPage from "./pages/BingoUK90HostPage";
import BingoUK90PlayPage from "./pages/BingoUK90PlayPage";
import RaffleDisplayPage from "./pages/RaffleDisplayPage";
import RaffleHostPage from "./pages/RaffleHostPage";
import CloakroomRaffleHostPage from "./pages/CloakroomRaffleHostPage";
import CloakroomRaffleDisplayPage from "./pages/CloakroomRaffleDisplayPage";
import ClubTicketsPage from "./pages/ClubTicketsPage";
import EventTicketsPage from "./pages/EventTicketsPage";
import TicketCheckinPage from "./pages/TicketCheckinPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import DancerPaymentPage from "./pages/DancerPaymentPage";
import PaperBingoHostPage from "./pages/PaperBingoHostPage";
import ClubBingoAdminPage from "./pages/ClubBingoAdminPage";
import ArtistsPage from "./pages/ArtistsPage";
import ArtistDetailPage from "./pages/ArtistDetailPage";
import AddEditArtistPage from "./pages/AddEditArtistPage";
import BingoDisplayPage from "./pages/BingoDisplayPage";
import PhotoWallUploadPage from "./pages/PhotoWallUploadPage";
import PhotoWallDisplayPage from "./pages/PhotoWallDisplayPage";
import EmbedBookingWidget from "./pages/EmbedBookingWidget";
import ShopPage from "./pages/ShopPage";
import ShopCheckoutPage from "./pages/ShopCheckoutPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import MembershipPage from "./pages/MembershipPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import AboutUsPage from "./pages/AboutUsPage";

// Helper component to wrap pages with transitions
function WithTransition({ children, variant = "default" }) {
  if (variant === "slide") {
    return <SlideTransition>{children}</SlideTransition>;
  }
  return <PageTransition>{children}</PageTransition>;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Pages without navbar/footer
  const authPages = ['/login', '/register', '/auth/callback'];
  const isDJLivePage = location.pathname.includes('/dj-live') || location.pathname.includes('/dj-console') || location.pathname.includes('/dj/display') || location.pathname.includes('/dj/projector') || location.pathname.includes('/display') || location.pathname.includes('/projector') || location.pathname.includes('/raffle/display') || location.pathname.includes('/checkin') || location.pathname.includes('/photo-wall');
  const showLayout = !authPages.includes(location.pathname) && !isDJLivePage;

  return (
    <div className="flex flex-col min-h-screen">
      {showLayout && <Navbar />}
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* Home */}
            <Route path="/" element={<WithTransition><HomePage /></WithTransition>} />
            
            {/* Auth Pages - No transition needed */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Directory Pages */}
            <Route path="/clubs" element={<WithTransition><ClubsPage /></WithTransition>} />
            <Route path="/clubs/:id" element={<WithTransition variant="slide"><ClubDetailPage /></WithTransition>} />
            <Route path="/instructors" element={<WithTransition><InstructorsPage /></WithTransition>} />
            <Route path="/instructors/:id" element={<WithTransition variant="slide"><InstructorProfilePage /></WithTransition>} />
            <Route path="/dances/popular" element={<WithTransition><PopularDancesPage /></WithTransition>} />
            <Route path="/events" element={<WithTransition><EventsPage /></WithTransition>} />
            <Route path="/events/:hostSlug/:eventSlug" element={<WithTransition variant="slide"><EventDetailPage /></WithTransition>} />
            <Route path="/events/:id" element={<WithTransition variant="slide"><EventDetailPage /></WithTransition>} />
            <Route 
              path="/events/:eventId/checkin" 
              element={
                <ProtectedRoute>
                  <TicketCheckinPage />
                </ProtectedRoute>
              } 
            />
            <Route path="/weekends" element={<WithTransition><WeekendsPage /></WithTransition>} />
            <Route path="/weekends/terms" element={<WithTransition><WeekendTermsPage /></WithTransition>} />
            <Route path="/weekends/:slug" element={<WithTransition variant="slide"><WeekendDetailPage /></WithTransition>} />
            <Route path="/embed/weekends/:slug" element={<EmbedBookingWidget />} />
            <Route path="/shop" element={<WithTransition><ShopPage /></WithTransition>} />
            <Route path="/shop/checkout" element={<WithTransition><ShopCheckoutPage /></WithTransition>} />
            <Route path="/shop/track" element={<WithTransition><OrderTrackingPage /></WithTransition>} />
            <Route path="/membership" element={<WithTransition><MembershipPage /></WithTransition>} />
            <Route path="/blog" element={<WithTransition><BlogPage /></WithTransition>} />
            <Route path="/blog/:slug" element={<WithTransition variant="slide"><BlogPostPage /></WithTransition>} />
            <Route path="/about-us" element={<WithTransition><AboutUsPage /></WithTransition>} />
            <Route path="/my-booking" element={<WithTransition><ManageBookingPage /></WithTransition>} />
            <Route path="/pay/:token" element={<DancerPaymentPage />} />
            <Route path="/djs" element={<WithTransition><DJsPage /></WithTransition>} />
            <Route path="/djs/:id" element={<WithTransition variant="slide"><DJDetailPage /></WithTransition>} />
            <Route path="/artists" element={<WithTransition><ArtistsPage /></WithTransition>} />
            <Route path="/artists/:artistSlug" element={<WithTransition variant="slide"><ArtistDetailPage /></WithTransition>} />
            <Route path="/classes" element={<WithTransition><ClassesPage /></WithTransition>} />
            
            {/* DJ Live Request System - Club-based routes (no transitions for live pages) */}
            <Route path="/club/:clubSlug/dj-live" element={<DJLivePage mode="club" />} />
            <Route path="/dj/display/:clubSlug" element={<DJDisplayPage mode="club" />} />
            <Route path="/dj/projector/:clubSlug" element={<DJProjectorPage mode="club" />} />
            <Route
              path="/club/:clubSlug/dj-console"
              element={
                <ProtectedRoute>
                  <DJConsolePage mode="club" />
                </ProtectedRoute>
              }
            />
            
            {/* DJ Live Request System - DJ-based routes */}
            <Route path="/dj/:djSlug/live" element={<DJLivePage mode="dj" />} />
            <Route path="/dj/:djSlug/display" element={<DJDisplayPage mode="dj" />} />
            <Route path="/dj/:djSlug/projector" element={<DJProjectorPage mode="dj" />} />
            <Route
              path="/dj/:djSlug/console"
              element={
                <ProtectedRoute>
                  <DJConsolePage mode="dj" />
                </ProtectedRoute>
              }
            />
            
            {/* Photo Wall */}
            <Route path="/club/:clubSlug/photo-wall" element={<PhotoWallUploadPage />} />
            <Route path="/club/:clubSlug/photo-wall/display" element={<PhotoWallDisplayPage />} />
            
            {/* Club Bingo Admin */}
            <Route path="/club/:clubSlug/bingo/admin" element={<ClubBingoAdminPage />} />
            
            {/* Dashboard & Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <WithTransition><DashboardPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/add-club"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditClubPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/edit-club/:id"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditClubPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/add-event"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditEventPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/edit-event/:id"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditEventPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/add-dj"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditDJPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/edit-dj/:id"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditDJPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/add-artist"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditArtistPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/edit-artist/:artistId"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditArtistPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/club/:clubId/tickets"
              element={
                <ProtectedRoute>
                  <WithTransition><ClubTicketsPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/event/:eventId/tickets"
              element={
                <ProtectedRoute>
                  <WithTransition><EventTicketsPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/club/:clubId/subscription/success"
              element={
                <ProtectedRoute>
                  <WithTransition><SubscriptionSuccessPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <WithTransition><AdminPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/add-artist"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditArtistPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/edit-artist/:artistId"
              element={
                <ProtectedRoute>
                  <WithTransition><AddEditArtistPage /></WithTransition>
                </ProtectedRoute>
              }
            />
            
            {/* Legal Pages */}
            <Route path="/cookies" element={<WithTransition><CookiePolicyPage /></WithTransition>} />
            <Route path="/privacy" element={<WithTransition><PrivacyPolicyPage /></WithTransition>} />
            
            {/* Bingo Game - Line Dance */}
            <Route path="/bingo" element={<WithTransition><BingoJoinPage /></WithTransition>} />
            <Route path="/bingo/host" element={<BingoHostPage />} />
            <Route path="/bingo/host/:gameCode" element={<BingoHostPage />} />
            <Route path="/bingo/play/:gameCode" element={<BingoPlayPage />} />
            <Route path="/bingo/display/:gameCode" element={<BingoDisplayPage />} />
            
            {/* Bingo Game - UK 90-Ball */}
            <Route path="/bingo/uk90" element={<BingoUK90HostPage />} />
            <Route path="/bingo/uk90/host" element={<BingoUK90HostPage />} />
            <Route path="/bingo/uk90/host/:gameCode" element={<BingoUK90HostPage />} />
            <Route path="/bingo/uk90/play/:gameCode" element={<BingoUK90PlayPage />} />
            
            {/* Bingo Game - Paper Tickets (90 Dances) */}
            <Route path="/bingo/paper" element={<PaperBingoHostPage />} />
            <Route path="/bingo/paper/:gameCode" element={<PaperBingoHostPage />} />
            
            {/* Raffle Display (TV) */}
            <Route path="/raffle/display/:sessionCode" element={<RaffleDisplayPage />} />
            
            {/* Raffle Host */}
            <Route path="/raffle/host" element={<RaffleHostPage />} />
            <Route path="/raffle/host/:sessionCode" element={<RaffleHostPage />} />

            {/* Cloakroom Ticket Raffle */}
            <Route path="/cloakroom/host" element={<CloakroomRaffleHostPage />} />
            <Route path="/cloakroom/host/:sessionCode" element={<CloakroomRaffleHostPage />} />
            <Route path="/cloakroom/display/:sessionCode" element={<CloakroomRaffleDisplayPage />} />
          </Routes>
        </AnimatePresence>
      </main>
      {showLayout && <Footer />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <TicketCartProvider>
              <AppRouter />
              <TicketCartDrawer />
              <FloatingCartButton />
              <CookieConsent />
              <Toaster position="top-right" richColors />
            </TicketCartProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
